import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { useFeatureVisibility } from '@/contexts/FeatureVisibilityContext';
import { useCurrency } from '@/hooks/useCurrency';
import { asApiError, type SafetyStockPolicyRow } from '@/services/safetyStockService';
import { useSafetyStockPolicyMutations } from '../hooks/useSafetyStockPolicies';
import {
  formatDays,
  formatPercentFraction,
  formatSafetyStockMethod,
  formatUnits,
  SAFETY_STOCK_METHODS,
  toNumber,
} from '../utils/format';
import {
  fractionToPercentInput,
  percentInputToFraction,
} from '../utils/configValidation';

const fieldClass =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-stocker-primary focus:outline-none focus:ring-1 focus:ring-stocker-primary';

interface Props {
  row: SafetyStockPolicyRow;
  onClose: () => void;
}

export function SkuDetailPanel({ row, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const { format, symbol } = useCurrency();
  const locale = i18n.language || 'es';
  const { isActionEnabled } = useFeatureVisibility();
  const applyEnabled = isActionEnabled('safety_stock.apply', false);
  const mutations = useSafetyStockPolicyMutations();

  const [method, setMethod] = useState(row.method || '');
  const [serviceLevelPct, setServiceLevelPct] = useState(
    fractionToPercentInput(row.service_level),
  );
  const [coverageDays, setCoverageDays] = useState('');
  const [previewSs, setPreviewSs] = useState<string | null>(null);
  const [previewMeta, setPreviewMeta] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [manualValue, setManualValue] = useState(String(row.safety_stock ?? ''));
  const [manualReason, setManualReason] = useState('');
  const [confirmManual, setConfirmManual] = useState(false);

  const [backtestResult, setBacktestResult] = useState<string | null>(null);

  useEffect(() => {
    setMethod(row.method || '');
    setServiceLevelPct(fractionToPercentInput(row.service_level));
    setCoverageDays('');
    setPreviewSs(null);
    setPreviewMeta(null);
    setBanner(null);
    setError(null);
    setManualValue(String(row.safety_stock ?? ''));
    setManualReason('');
    setConfirmManual(false);
    setBacktestResult(null);
  }, [row.id, row.method, row.service_level, row.safety_stock]);

  const grain = {
    product_id: row.product_id,
    warehouse_id: row.warehouse_id,
    supplier_id: row.supplier_id,
  };

  const buildOverrides = (): Record<string, unknown> => {
    const overrides: Record<string, unknown> = {};
    if (method) overrides.method = method;
    const fraction = percentInputToFraction(serviceLevelPct);
    if (fraction != null) overrides.service_level = fraction;
    const days = toNumber(coverageDays);
    if (days != null && days >= 0) overrides.configured_safety_days = days;
    return overrides;
  };

  const runPreview = async () => {
    setError(null);
    setBanner(null);
    try {
      const data = await mutations.preview.mutateAsync({
        ...grain,
        overrides: buildOverrides(),
      });
      setPreviewSs(
        data.preview_safety_stock != null ? String(data.preview_safety_stock) : null,
      );
      const calc = (data.engine?.calculation || {}) as Record<string, unknown>;
      const applied = calc.applied_method || calc.method;
      setPreviewMeta(
        applied
          ? t('safetyStock.products.detail.previewMethod', {
              method: formatSafetyStockMethod(String(applied), t),
            })
          : null,
      );
    } catch (err) {
      setError(asApiError(err).message);
    }
  };

  const runApply = async () => {
    setError(null);
    setBanner(null);
    try {
      await mutations.apply.mutateAsync({
        ...grain,
        overrides: buildOverrides(),
      });
      setBanner(t('safetyStock.products.detail.applyOk'));
    } catch (err) {
      setError(asApiError(err).message);
    }
  };

  const runManual = async () => {
    setError(null);
    setBanner(null);
    const value = toNumber(manualValue);
    if (value == null || value < 0) {
      setError(t('safetyStock.products.detail.manualInvalid'));
      return;
    }
    if (!manualReason.trim()) {
      setError(t('safetyStock.products.detail.reasonRequired'));
      return;
    }
    if (!confirmManual) {
      setConfirmManual(true);
      return;
    }
    try {
      await mutations.manual.mutateAsync({
        ...grain,
        safety_stock: value,
        reason: manualReason.trim(),
        product_supplier_id: row.product_supplier_id,
      });
      setBanner(t('safetyStock.products.detail.manualOk'));
      setConfirmManual(false);
    } catch (err) {
      setError(asApiError(err).message);
      setConfirmManual(false);
    }
  };

  const runBacktest = async (persist: boolean) => {
    setError(null);
    setBanner(null);
    setBacktestResult(null);
    try {
      const data = await mutations.backtest.mutateAsync({
        ...grain,
        persist,
      });
      const selection = (data.selection || {}) as Record<string, unknown>;
      const winner = selection.selected_method || selection.method || data.best_method;
      setBacktestResult(
        t('safetyStock.products.detail.backtestResult', {
          method: winner ? formatSafetyStockMethod(String(winner), t) : '—',
          persisted: persist
            ? t('safetyStock.products.detail.persistedYes')
            : t('safetyStock.products.detail.persistedNo'),
        }),
      );
      if (persist) setBanner(t('safetyStock.products.detail.backtestApplied'));
    } catch (err) {
      setError(asApiError(err).message);
    }
  };

  const busy =
    mutations.preview.isPending ||
    mutations.apply.isPending ||
    mutations.manual.isPending ||
    mutations.backtest.isPending;

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            {t('safetyStock.products.detail.title')}
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            {[row.product_code, row.product_name].filter(Boolean).join(' · ') ||
              `#${row.product_id}`}
            {' · '}
            {row.warehouse_code || row.warehouse_id}
            {' · '}
            {row.supplier_name || row.supplier_id}
          </p>
        </div>
        <button
          type="button"
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
          onClick={onClose}
        >
          {t('safetyStock.products.detail.close')}
        </button>
      </div>

      {error ? (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      ) : null}
      {banner ? (
        <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
          {banner}
        </div>
      ) : null}

      <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-sm">
        <div>
          <div className="text-gray-500">{t('safetyStock.products.cols.method')}</div>
          <div className="font-medium text-gray-900">
            {formatSafetyStockMethod(row.method, t)}
          </div>
        </div>
        <div>
          <div className="text-gray-500">{t('safetyStock.products.cols.csl')}</div>
          <div className="font-medium text-gray-900">
            {formatPercentFraction(row.service_level, locale)}
          </div>
        </div>
        <div>
          <div className="text-gray-500">{t('safetyStock.products.cols.safetyStock')}</div>
          <div className="font-medium tabular-nums text-gray-900">
            {formatUnits(row.safety_stock, locale)}
          </div>
        </div>
        <div>
          <div className="text-gray-500">{t('safetyStock.products.cols.value', { currencySymbol: symbol })}</div>
          <div className="font-medium tabular-nums text-gray-900">
            {toNumber(row.safety_stock_value) == null
              ? '—'
              : format(row.safety_stock_value)}
          </div>
        </div>
        <div>
          <div className="text-gray-500">{t('safetyStock.products.cols.coverageDays')}</div>
          <div className="font-medium tabular-nums text-gray-900">
            {formatDays(row.coverage_days, locale)}
          </div>
        </div>
        <div>
          <div className="text-gray-500">{t('safetyStock.products.cols.protection')}</div>
          <div className="font-medium tabular-nums text-gray-900">
            {formatDays(row.protection_period_days, locale)}
          </div>
        </div>
        <div>
          <div className="text-gray-500">{t('safetyStock.products.cols.updateType')}</div>
          <div className="font-medium text-gray-900">
            {row.update_type
              ? t(`safetyStock.updateTypes.${String(row.update_type).toUpperCase()}`, {
                  defaultValue: row.update_type,
                })
              : '—'}
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-3 rounded-lg border border-gray-100 p-3">
          <h3 className="text-sm font-semibold text-gray-900">
            {t('safetyStock.products.detail.overridesTitle')}
          </h3>
          <p className="text-xs text-gray-500">{t('safetyStock.products.detail.overridesHelp')}</p>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              {t('safetyStock.products.detail.method')}
            </span>
            <select
              className={fieldClass}
              value={method}
              onChange={(e) => setMethod(e.target.value)}
            >
              <option value="">{t('safetyStock.products.detail.methodDefault')}</option>
              {SAFETY_STOCK_METHODS.map((m) => (
                <option key={m} value={m}>
                  {formatSafetyStockMethod(m, t)}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              {t('safetyStock.products.detail.serviceLevel')}
            </span>
            <div className="relative">
              <input
                type="number"
                step="0.01"
                className={fieldClass}
                value={serviceLevelPct}
                onChange={(e) => setServiceLevelPct(e.target.value)}
              />
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-gray-400">
                %
              </span>
            </div>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              {t('safetyStock.products.detail.coverageDays')}
            </span>
            <input
              type="number"
              min={0}
              step="0.1"
              className={fieldClass}
              value={coverageDays}
              placeholder={t('safetyStock.products.detail.optional')}
              onChange={(e) => setCoverageDays(e.target.value)}
            />
          </label>

          <div className="flex flex-wrap gap-2 pt-1">
            <button
              type="button"
              className="rounded-md border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
              disabled={busy}
              onClick={() => void runPreview()}
            >
              {mutations.preview.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('safetyStock.products.detail.preview')
              )}
            </button>
            <button
              type="button"
              className="rounded-md bg-stocker-primary px-3 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
              disabled={busy || !applyEnabled}
              title={
                !applyEnabled ? t('safetyStock.apply.errors.applyDisabled') : undefined
              }
              onClick={() => void runApply()}
            >
              {mutations.apply.isPending
                ? t('safetyStock.products.detail.applying')
                : t('safetyStock.products.detail.apply')}
            </button>
          </div>

          {previewSs != null ? (
            <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
              <div>
                {t('safetyStock.products.detail.previewCompare', {
                  current: formatUnits(row.safety_stock, locale),
                  preview: formatUnits(previewSs, locale),
                })}
              </div>
              {previewMeta ? <div className="mt-1 text-xs">{previewMeta}</div> : null}
              <div className="mt-1 text-xs text-sky-800/80">
                {t('safetyStock.products.detail.previewNoPersist')}
              </div>
            </div>
          ) : null}
        </div>

        <div className="space-y-4">
          <div className="space-y-3 rounded-lg border border-gray-100 p-3">
            <h3 className="text-sm font-semibold text-gray-900">
              {t('safetyStock.products.detail.manualTitle')}
            </h3>
            <p className="text-xs text-gray-500">{t('safetyStock.products.detail.manualHelp')}</p>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-gray-700">
                {t('safetyStock.products.detail.manualValue')}
              </span>
              <input
                type="number"
                min={0}
                step="0.01"
                className={fieldClass}
                value={manualValue}
                onChange={(e) => {
                  setManualValue(e.target.value);
                  setConfirmManual(false);
                }}
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-gray-700">
                {t('safetyStock.products.detail.manualReason')}
              </span>
              <textarea
                className={fieldClass}
                rows={2}
                value={manualReason}
                onChange={(e) => {
                  setManualReason(e.target.value);
                  setConfirmManual(false);
                }}
              />
            </label>
            <button
              type="button"
              className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
              disabled={busy || !applyEnabled}
              onClick={() => void runManual()}
            >
              {confirmManual
                ? t('safetyStock.products.detail.manualConfirm')
                : t('safetyStock.products.detail.manualCta')}
            </button>
          </div>

          <div className="space-y-3 rounded-lg border border-gray-100 p-3">
            <h3 className="text-sm font-semibold text-gray-900">
              {t('safetyStock.products.detail.backtestTitle')}
            </h3>
            <p className="text-xs text-gray-500">{t('safetyStock.products.detail.backtestHelp')}</p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="rounded-md border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
                disabled={busy}
                onClick={() => void runBacktest(false)}
              >
                {t('safetyStock.products.detail.backtestRun')}
              </button>
              <button
                type="button"
                className="rounded-md border border-stocker-primary px-3 py-2 text-sm font-medium text-stocker-primary hover:bg-stocker-primary/5 disabled:opacity-50"
                disabled={busy || !applyEnabled}
                onClick={() => void runBacktest(true)}
              >
                {t('safetyStock.products.detail.backtestApply')}
              </button>
            </div>
            {backtestResult ? (
              <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800">
                {backtestResult}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
